'use client'

import { api } from '../../convex/_generated/api'
import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import {
  HomeIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  BeakerIcon,
  DocumentChartBarIcon,
  DocumentArrowDownIcon,
  HeartIcon,
  ChevronDoubleLeftIcon,
  ChevronDownIcon,
  Square3Stack3DIcon,
  Squares2X2Icon,
  GlobeAltIcon,
  TagIcon,
  ChatBubbleLeftRightIcon,
  LockClosedIcon,
  InboxIcon,
} from '@heroicons/react/24/outline'
import { Bot } from 'lucide-react'

/** Lucide's Bot icon with stroke weight tuned to match heroicons outline (1.5)
 *  so it sits visually alongside the rest of the sidebar nav items. */
function BotIcon({ className }: { className?: string }) {
  return <Bot className={className} strokeWidth={1.5} />
}
import UserMenu from './UserMenu'
import OrgSwitcher from './OrgSwitcher'
import Logo from './ui/Logo'
import BrandLogoSwap from './partner/BrandLogoSwap'
import { useConvexAuth, useQuery } from 'convex/react'
import useOrganizationPermissions, { FEATURE, MODULE, type ModuleKey } from '@/hooks/useOrganizationPermissions'
import useSidebarSubmenus, { type SubmenuKey } from '@/hooks/useSidebarSections'
import { usePartnerBranding } from '@/hooks/usePartnerBranding'
import OnboardingCheckoutModal from './onboarding/OnboardingCheckoutModal'

type SidebarRole = 'super_admin' | 'partner'

type SidebarItemKey =
  | 'dashboard' | 'brands' | 'reports' | 'pdfReports' | 'citations'
  | 'projects' | 'prompts' | 'verticals'
  | 'overview' | 'content' | 'mentions' | 'vitals'
  | 'traffic' | 'integrations'
  | 'inbox' | 'agents'

type SidebarGroupKey = 'brandVisibility' | 'analysisEngine' | 'domainImpact' | 'connectivity' | 'actions'

interface SidebarLeafItem {
  nameKey: SidebarItemKey
  href: string
  icon: React.ComponentType<{ className?: string }>
  lockedFeature?: FEATURE
  /** Exact match for active state (used for `/publisher` which is a parent of every publisher page). */
  exactActive?: boolean
  /** If set, item is hidden unless the current user has at least one of these roles. */
  requireRoles?: SidebarRole[]
}

interface SidebarSubmenu {
  key: SubmenuKey
  labelKey: SidebarGroupKey
  items: SidebarLeafItem[]
}

type SidebarGroup =
  | { kind: 'flat'; key: string; labelKey: SidebarGroupKey; items: SidebarLeafItem[]; visibleModule?: ModuleKey }
  | { kind: 'nested'; key: string; labelKey: SidebarGroupKey; submenus: SidebarSubmenu[]; visibleModule?: ModuleKey }

const SIDEBAR_LAYOUT: SidebarGroup[] = [
  {
    kind: 'flat',
    key: 'brand_visibility',
    labelKey: 'brandVisibility',
    items: [
      { nameKey: 'dashboard', href: '/dashboard', icon: HomeIcon },
      { nameKey: 'brands', href: '/brands', icon: BuildingOfficeIcon },
      { nameKey: 'reports', href: '/reports', icon: DocumentChartBarIcon, lockedFeature: FEATURE.WEEKLY_REPORTS },
      { nameKey: 'pdfReports', href: '/pdf-reports', icon: DocumentArrowDownIcon, lockedFeature: FEATURE.PDF_REPORT_EXPORT, requireRoles: ['super_admin', 'partner'] },
      { nameKey: 'citations', href: '/citations', icon: ChartBarIcon },
    ],
  },
  {
    kind: 'flat',
    key: 'analysis_engine',
    labelKey: 'analysisEngine',
    items: [
      { nameKey: 'projects', href: '/analysis', icon: BeakerIcon },
      { nameKey: 'prompts', href: '/templates', icon: DocumentTextIcon },
      { nameKey: 'verticals', href: '/verticals', icon: TagIcon },
      // { nameKey: 'sources', href: '/sources', icon: LinkIcon, lockedFeature: FEATURE.PUBLISHER_TOPIC_INSIGHTS },
    ],
  },
  {
    kind: 'flat',
    key: 'domain_impact',
    labelKey: 'domainImpact',
    items: [
      { nameKey: 'overview', href: '/publisher', icon: Squares2X2Icon, exactActive: true, lockedFeature: FEATURE.VITALS_PAGE },
      { nameKey: 'content', href: '/content', icon: DocumentTextIcon },
      { nameKey: 'mentions', href: '/mentions', icon: ChatBubbleLeftRightIcon },
      { nameKey: 'vitals', href: '/vitals', icon: HeartIcon, lockedFeature: FEATURE.VITALS_PAGE },
    ],
  },
  {
    kind: 'flat',
    key: 'connectivity',
    labelKey: 'connectivity',
    items: [
      { nameKey: 'traffic', href: '/traffic', icon: GlobeAltIcon, lockedFeature: FEATURE.INTEGRATIONS },
      { nameKey: 'integrations', href: '/integration-hub', icon: Square3Stack3DIcon, lockedFeature: FEATURE.INTEGRATIONS },
    ],
  },
  {
    kind: 'flat',
    key: 'actions',
    labelKey: 'actions',
    // This group is fully hidden when the org does not have the agents module.
    // Do NOT use lockedFeature here -- agents require operator enablement and
    // are not upsellable via the upgrade modal.
    visibleModule: MODULE.AGENTS,
    items: [
      { nameKey: 'inbox', href: '/agents/inbox', icon: InboxIcon },
      // exactActive avoids /agents lighting up while the user is on
      // /agents/inbox or /agents/reddit/settings (both start with /agents).
      { nameKey: 'agents', href: '/agents', icon: BotIcon, exactActive: true },
    ],
  },
]

function NavItemLink({
  item,
  name,
  upgradeRequired,
  collapsed,
  pathname,
  paddingClass,
  isLocked,
  onLockedClick,
}: {
  item: SidebarLeafItem
  /** Translated display name for this item. */
  name: string
  /** Translated "upgrade required" suffix shown in the title of locked items. */
  upgradeRequired: string
  collapsed: boolean
  pathname: string
  /** Override the default horizontal padding (used by submenus to indent items). */
  paddingClass?: string
  /** When true, render the item with a lock indicator and call `onLockedClick` instead of navigating. */
  isLocked?: boolean
  onLockedClick?: () => void
}) {
  const isActive = !isLocked && (item.exactActive
    ? pathname === item.href
    : pathname.startsWith(item.href))
  const padding = collapsed ? 'p-3' : (paddingClass ?? 'px-2')
  const lockedTitle = isLocked ? `${name} (${upgradeRequired})` : name

  if (isLocked) {
    return (
      <a
        title={lockedTitle}
        onClick={onLockedClick}
        className={`sidebar-nav-item cursor-pointer ${padding}`}
        style={{ color: 'var(--color-text-muted)', opacity: 0.7 }}
      >
        <item.icon className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5'} ${collapsed ? 'mx-auto' : 'mr-3'}`} />
        {!collapsed && <span className="text-sm font-medium flex-1 text-left">{name}</span>}
        {!collapsed && (
          <LockClosedIcon className="w-3.5 h-3.5 ml-2 shrink-0" style={{ color: 'var(--color-text-muted)' }} />
        )}
      </a>
    )
  }

  return (
    <Link
      href={item.href}
      title={name}
      className={`sidebar-nav-item ${isActive ? 'active' : ''} ${padding}`}
    >
      <item.icon className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5'} ${collapsed ? 'mx-auto' : 'mr-3'}`} />
      {!collapsed && <span className="text-sm font-medium flex-1">{name}</span>}
    </Link>
  )
}

function GroupLabel({ label }: { label: string }) {
  return (
    <div
      className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest font-semibold select-none"
      style={{ color: 'var(--color-text-muted)' }}
    >
      {label}
    </div>
  )
}

function Submenu({
  submenu,
  label,
  visibleItems,
  collapsed,
  isExpanded,
  isActive,
  onToggle,
  renderItem,
}: {
  submenu: SidebarSubmenu
  /** Translated label for the submenu header. */
  label: string
  visibleItems: SidebarLeafItem[]
  collapsed: boolean
  pathname: string
  isExpanded: boolean
  /** Whether this submenu owns the current route (highlights the header). */
  isActive: boolean
  onToggle: () => void
  /** Renders one leaf item — supplied by the parent so it can apply locked state. */
  renderItem: (item: SidebarLeafItem) => React.ReactNode
}) {
  if (collapsed) return null
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-[var(--color-surface-hover)] transition-colors"
        style={{
          color: isActive ? 'var(--color-lime)' : 'var(--color-text-muted)',
          borderLeft: isActive ? '3px solid var(--color-lime)' : '3px solid transparent',
        }}
      >
        <span>{label}</span>
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-200 ease-out ${isExpanded ? '' : '-rotate-90'}`}
          style={{ color: isActive ? 'var(--color-lime)' : 'var(--color-text-muted)' }}
        />
      </button>
      {/* Animated reveal via grid-template-rows — animates 0fr → 1fr so we don't
          guess a max-height. Inner overflow-hidden clips during transition. */}
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
        aria-hidden={!isExpanded}
      >
        <div className="overflow-hidden">
          <div className="space-y-1 mt-1">{visibleItems.map(renderItem)}</div>
        </div>
      </div>
    </div>
  )
}

function SidebarGroupRenderer({
  group,
  collapsed,
  pathname,
  has,
  userRoles,
  submenuExpanded,
  toggleSubmenu,
  activeSubmenu,
  onLockedClick,
  tSidebar,
}: {
  group: SidebarGroup
  collapsed: boolean
  pathname: string
  has: (options: { feature: FEATURE }) => boolean
  userRoles: SidebarRole[]
  submenuExpanded: (key: SubmenuKey) => boolean
  toggleSubmenu: (key: SubmenuKey) => void
  activeSubmenu: SubmenuKey | null
  onLockedClick: () => void
  tSidebar: ReturnType<typeof useTranslations<'sidebar'>>
}) {
  const isItemLocked = (item: SidebarLeafItem) =>
    !!item.lockedFeature && !has({ feature: item.lockedFeature })

  const isItemVisible = (item: SidebarLeafItem) =>
    !item.requireRoles || item.requireRoles.some((r) => userRoles.includes(r))

  const upgradeRequired = tSidebar('upgradeRequired')

  const renderItems = (items: SidebarLeafItem[], paddingClass?: string) =>
    items.filter(isItemVisible).map((item) => (
      <NavItemLink
        key={item.nameKey}
        item={item}
        name={tSidebar(`items.${item.nameKey}`)}
        upgradeRequired={upgradeRequired}
        collapsed={collapsed}
        pathname={pathname}
        paddingClass={paddingClass}
        isLocked={isItemLocked(item)}
        onLockedClick={onLockedClick}
      />
    ))

  const groupLabel = tSidebar(`groups.${group.labelKey}`)

  if (group.kind === 'flat') {
    if (collapsed) {
      return <div className="space-y-1 py-2">{renderItems(group.items)}</div>
    }
    return (
      <div>
        <GroupLabel label={groupLabel} />
        <div className="space-y-1">{renderItems(group.items)}</div>
      </div>
    )
  }

  // Nested group (Insights).
  if (collapsed) {
    // Flatten all submenu items into a single icon stack so dividers between
    // top-level groups stay aligned with the four-section structure.
    const flat = group.submenus.flatMap((s) => s.items)
    return <div className="space-y-1 py-2">{renderItems(flat)}</div>
  }

  return (
    <div>
      <GroupLabel label={groupLabel} />
      <div className="space-y-0.5">
        {group.submenus.map((submenu) => (
          <Submenu
            key={submenu.key}
            submenu={submenu}
            label={tSidebar(`groups.${submenu.labelKey}`)}
            visibleItems={submenu.items.filter(isItemVisible)}
            collapsed={collapsed}
            pathname={pathname}
            isExpanded={submenuExpanded(submenu.key)}
            isActive={activeSubmenu === submenu.key}
            onToggle={() => toggleSubmenu(submenu.key)}
            renderItem={(item) => (
              <NavItemLink
                key={item.nameKey}
                item={item}
                name={tSidebar(`items.${item.nameKey}`)}
                upgradeRequired={upgradeRequired}
                collapsed={collapsed}
                pathname={pathname}
                paddingClass="px-2"
                isLocked={isItemLocked(item)}
                onLockedClick={onLockedClick}
              />
            )}
          />
        ))}
      </div>
    </div>
  )
}

export default function Sidebar({ onCollapseChange }: { onCollapseChange?: (collapsed: boolean) => void }) {
  const tSidebar = useTranslations('sidebar')
  const [collapsed, setCollapsed] = useState(false)
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)
  const pathname = usePathname() ?? '/'
  // Gate on Convex auth, not Clerk's isSignedIn (sign-out race, see src/lib/authError.ts)
  const { isAuthenticated } = useConvexAuth()
  const organizations = useQuery(api.organizations.getOrganizations, isAuthenticated ? {} : 'skip')
  const organization = organizations?.[0]
  const activePlan = useQuery(
    api.features.plans.queries.getPlanById,
    isAuthenticated && organization?.planId ? { planId: organization.planId } : 'skip'
  )
  const { has, hasModule } = useOrganizationPermissions()
  const { isExpanded: submenuExpanded, toggle: toggleSubmenu, activeSubmenu } = useSidebarSubmenus()
  // Partner-branding swap: when the user's org is partner-affiliated, use the
  // partner's logo + name instead of the 42a chrome. `undefined` while loading
  // (keep 42a defaults); `null` for non-partner orgs (also 42a defaults).
  const branding = usePartnerBranding()

  // Role detection for items gated by requireRoles.
  // - super_admin: 42A internal staff (operator_42a or agent_42a userType — same check used by /admin and /tasks)
  // - partner: user belongs to a partner org (api.partners.isCurrentUserPartner)
  //   NOTE: must use the SAFE boolean query, not getMyPartner — the latter throws
  //   "User is not associated with a partner" for non-partner users, which crashes
  //   the entire app via useQuery's thrown promise.
  const currentUser = useQuery(api.users.getCurrentUser, isAuthenticated ? {} : 'skip')
  const isSuperAdmin =
    currentUser?.userType === 'operator_42a' || currentUser?.userType === 'agent_42a'
  const isPartner = useQuery(
    api.partners.isCurrentUserPartner,
    isAuthenticated ? {} : 'skip',
  ) === true
  const userRoles: SidebarRole[] = [
    ...(isSuperAdmin ? ['super_admin' as const] : []),
    ...(isPartner ? ['partner' as const] : []),
  ]

  const handleCollapseChange = () => {
    setCollapsed((ps) => {
      onCollapseChange?.(!ps)
      return !ps
    })
  }

  // Auto-collapse on small screens.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1024px)')

    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        setCollapsed(true)
        onCollapseChange?.(true)
      }
    }

    handleResize(mediaQuery)
    mediaQuery.addEventListener('change', handleResize)
    return () => mediaQuery.removeEventListener('change', handleResize)
  }, [onCollapseChange])

  return (
    <div className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="flex flex-col h-full">
        {/* Logo + Org Switcher */}
        <div className={`pt-3 pb-2 border-b ${collapsed ? 'px-2' : 'px-2'}`} style={{ borderColor: 'var(--color-border)' }}>
          <div className={`grid items-center w-full ${collapsed ? 'grid-cols-1' : 'grid-cols-[1fr_auto]'}`}>
            <div
              className={`transition-all flex items-center ${collapsed ? 'justify-center' : 'justify-start'}`}
            >
              <BrandLogoSwap
                partnerLogoUrl={branding?.logoUrl}
                partnerLogoDarkUrl={branding?.logoDarkUrl}
                width={collapsed ? 40 : 140}
                height={collapsed ? 32 : 40}
              />
            </div>
            <div className="hidden lg:flex items-center h-full justify-end text-sm">
              <ChevronDoubleLeftIcon className={`cursor-pointer w-4 h-4 transition-all ${collapsed ? 'mx-auto rotate-180 mt-1' : ''}`} onClick={handleCollapseChange} />
            </div>
          </div>

          <div className="mt-1">
            <OrgSwitcher
              collapsed={collapsed}
              planName={activePlan?.name}
              isSuspended={organization?.isSuspended}
            />
          </div>

          {!collapsed && organization?.freeTrial?.enabled && (
            <div className="mt-1 px-2">
              <span className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
                {tSidebar('freeTrial', { days: Math.max(0, Math.ceil((organization.freeTrial.endDate - Date.now()) / (24 * 60 * 60 * 1000))) })}
              </span>
            </div>
          )}
        </div>

        {/* Navigation — four top-level groups, each rendered as a static greyed
            label with its items below. Insights is the only group with
            collapsable submenus. Narrow mode: drop labels and render a flat
            icon list with dividers between groups. */}
        <nav
          className={`flex-1 pb-4 overflow-y-auto ${
            collapsed
              ? 'divide-y divide-[var(--color-border)]'
              : 'space-y-2'
          }`}
        >
          {SIDEBAR_LAYOUT.filter(
            (group) => !group.visibleModule || hasModule(group.visibleModule)
          ).map((group) => (
            <SidebarGroupRenderer
              key={group.key}
              group={group}
              collapsed={collapsed}
              pathname={pathname}
              has={has}
              userRoles={userRoles}
              submenuExpanded={submenuExpanded}
              toggleSubmenu={toggleSubmenu}
              activeSubmenu={activeSubmenu}
              onLockedClick={() => setIsCheckoutOpen(true)}
              tSidebar={tSidebar}
            />
          ))}
        </nav>

        {/* User Menu Section */}
        <div className="p-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <UserMenu collapsed={collapsed} dropdownDirection="up" />
        </div>

        {/* Powered-by-42A chip — only shown to customers of a partner.
            Sits beneath the user menu so the partner's logo (at the top of
            the sidebar) and 42A's attribution (here) are visually balanced. */}
        {branding && (
          <div
            className="px-2 pt-2 pb-2 flex items-center justify-center gap-1.5 border-t"
            style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}
          >
            <Logo width={28} height={10} />
            {!collapsed && (
              <span className="text-[10px] leading-none whitespace-nowrap">
                {tSidebar('poweredBy')}
              </span>
            )}
          </div>
        )}
      </div>

      <OnboardingCheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        onComplete={() => setIsCheckoutOpen(false)}
        skipAutoComplete
      />
    </div>
  )
}
